/* ==========================================================================
   Beyond Memories — floating chat launcher
   Loaded by snippets/bm-chat.liquid.

   Responsibilities:
   1. Open/close the bubble menu (click, outside click, Escape).
   2. Hide Shopify Inbox's own launcher so the storefront shows a single entry point.
   3. Open Inbox when the merchant's menu item is clicked.

   Inbox's launcher has moved around a lot. Known shapes, newest first:
     • Web component  — #ShopifyChat has a shadowRoot; launcher is `.chat-app > button`
     • React          — #ShopifyChat button[aria-controls="chat-ui"]
     • iframe (2022)  — #ShopifyChat.contentWindow.document `.chat-toggle--bottom-right`
   We never guess with a bare `button` selector: clicking the wrong control inside the
   chat panel would be worse than failing loudly.
   ========================================================================== */

(function () {
  'use strict';

  var root = document.querySelector('[data-bm-chat]');
  if (!root) return;

  // Resolve by attribute with fallbacks. A markup/script marker mismatch is otherwise a silent
  // dead button: the handlers below simply never bind and nothing is logged.
  function pick(node, attribute, selector) {
    return node.querySelector(attribute) || node.querySelector(selector) || null;
  }

  var toggle = pick(root, '[data-bm-chat-toggle]', '.bm-chat__toggle');
  var panel = pick(root, '[data-bm-chat-panel]', '#bm-chat-panel, .bm-chat__panel');
  var inboxItem = pick(root, '[data-bm-inbox]', '.bm-chat__item--inbox');

  /* ================================================================ menu */

  var isOpen = false;

  function setOpen(next) {
    next = Boolean(next);
    if (next === isOpen) return;
    isOpen = next;
    root.classList.toggle('is-open', next);
    if (toggle) toggle.setAttribute('aria-expanded', String(next));
    if (panel) panel.setAttribute('aria-hidden', String(!next));
  }

  // Only `toggle` is required. The panel is optional so a missing panel degrades to a visible
  // no-op rather than silently killing the outside-click and Escape handlers too.
  if (toggle) {
    toggle.addEventListener('click', function () {
      setOpen(!isOpen);
    });

    document.addEventListener('click', function (event) {
      if (!isOpen) return;
      if (root.contains(event.target)) return;
      setOpen(false);
    });

    root.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape' || !isOpen) return;
      setOpen(false);
      if (toggle) toggle.focus();
    });

    // Loud failure instead of a dead button.
    if (!panel && root.getAttribute('data-single-item') === 'false' && window.console && console.warn) {
      console.warn(
        '[bm-chat] Launcher found but the menu panel is missing — click would do nothing. ' +
          'Check that bm-chat.liquid matches this script version.'
      );
    }
  }

  /* ============================================================== Inbox */

  var HOST_IDS = ['ShopifyChat', 'shopify-chat', 'shopify-chat-button'];
  var HIDDEN_ATTR = 'data-bm-chat-hidden';
    var warned = false;
  var hintEl = inboxItem ? inboxItem.querySelector('.bm-chat__hint') : null;
  var hintOriginal = hintEl ? hintEl.textContent : null;

  /** Is the Inbox web component actually live on this page? */
  function inboxReady() {
    if (typeof customElements !== 'undefined' && customElements.get('shopify-chat')) return true;
    var host = inboxHost();
    return Boolean(host && host.shadowRoot);
  }

  function inboxHost() {
    for (var i = 0; i < HOST_IDS.length; i++) {
      var byId = document.getElementById(HOST_IDS[i]);
      if (byId) return byId;
    }
    return document.querySelector('shopify-chat');
  }

  /** Depth-first search through the host and every nested shadow root. */
  function deepQuery(root, selectors) {
    for (var i = 0; i < selectors.length; i++) {
      var hit = root.querySelector(selectors[i]);
      if (hit) return hit;
    }
    var all = root.querySelectorAll('*');
    for (var j = 0; j < all.length; j++) {
      if (!all[j].shadowRoot) continue;
      var nested = deepQuery(all[j].shadowRoot, selectors);
      if (nested) return nested;
    }
    return null;
  }

  // Inbox has shipped several launcher shapes. `.activator` is the current one; the global
  // search is what makes "minimise then reopen" work, because after minimise the launcher is
  // re-created deeper in the tree rather than at the host's shadow root.
  var LAUNCHER_SELECTORS = [
    '.activator',
    '[class*="activator"]',
    '.chat-app > button',
    '.chat-app button[aria-controls]',
    'button[aria-controls="chat-ui"]',
    'button[aria-label*="Open chat" i]',
  ];

  function inboxLauncher() {
    var host = inboxHost();
    if (!host) return null;

    // 2022 iframe generation. Cross-origin access throws; that is a "cannot drive it" case.
    if (host.tagName === 'IFRAME') {
      try {
        var doc = host.contentWindow && host.contentWindow.document;
        return doc ? doc.querySelector('.chat-toggle--bottom-right') : null;
      } catch (error) {
        return null;
      }
    }

    return deepQuery(host.shadowRoot || host, LAUNCHER_SELECTORS);
  }

  /** Horizon and similar themes host <shopify-chat> inside a <theme-drawer>. */
  function chatDrawer() {
    return document.getElementById('chat-drawer') || document.querySelector('theme-drawer#chat-drawer');
  }

  /**
   * Every launcher-like node in the host's shadow tree.
   *
   * Inbox renders more than one: the `.activator` button AND a separate
   * `div.activator-nudge` bubble. Hiding only the first left the nudge sitting on top of our
   * toggle — invisible, but it swallowed the clicks. Verified on a live theme: the hit-test at
   * our button's centre returned `DIV.activator-nudge` and our menu never opened.
   */
  function launcherCandidates() {
    var host = inboxHost();
    if (!host || host.tagName === 'IFRAME') return [];

    var out = [];
    var roots = shadowRootsOf(host);
    for (var i = 0; i < roots.length; i++) {
      for (var s = 0; s < LAUNCHER_SELECTORS.length; s++) {
        var matched = roots[i].querySelectorAll(LAUNCHER_SELECTORS[s]);
        for (var k = 0; k < matched.length; k++) {
          if (out.indexOf(matched[k]) === -1) out.push(matched[k]);
        }
      }
    }
    return out;
  }

  /**
   * All shadow roots reachable from the host, outermost first.
   */
  function shadowRootsOf(host) {
    var roots = [host.shadowRoot || host];
    for (var i = 0; i < roots.length; i++) {
      var all = roots[i].querySelectorAll('*');
      for (var j = 0; j < all.length; j++) {
        if (all[j].shadowRoot) roots.push(all[j].shadowRoot);
      }
    }
    return roots;
  }

  /**
   * Every node on the path from the chat panel down to its iframe.
   *
   * These must never be hidden. `visibility` inherits, so hiding an ANCESTOR of the panel also
   * hides the panel — the container matched `[class*="activator"]` on one store and the whole
   * chat body vanished, leaving only the header drawn outside it. Reported from production.
   */
  function panelAncestors() {
    var host = inboxHost();
    if (!host) return [];

    var iframe = null;
    var roots = shadowRootsOf(host);
    for (var i = 0; i < roots.length && !iframe; i++) {
      var frames = roots[i].querySelectorAll('iframe');
      for (var j = 0; j < frames.length; j++) {
        var r = frames[j].getBoundingClientRect();
        // The chat body is large; skip the tiny pixel/analytics iframes.
        if (r.width >= 200 && r.height >= 120) {
          iframe = frames[j];
          break;
        }
      }
    }
    if (!iframe) return [];

    var out = [];
    var el = iframe;
    var guard = 0;
    while (el && guard++ < 20) {
      out.push(el);
      if (el.parentElement) {
        el = el.parentElement;
      } else {
        var root = el.getRootNode();
        el = root && root.host ? root.host : null;
      }
    }
    return out;
  }

  /**
   * Hides every Inbox launcher node in place.
   *
   * `visibility: hidden` rather than `display: none` on purpose: the widget measures the
   * launcher's box to decide where to place the chat panel, so the element has to keep its
   * geometry. A hidden element is out of the tab order and cannot be clicked by a customer,
   * but `element.click()` still dispatches, which is what we need.
   */
  function hideLaunchers() {
    var host = inboxHost();
    if (!host) return false;

    // Never touch anything while the panel is open: the same class names can appear on the
    // panel's own controls, and hiding those would remove the customer's close button.
    if (host.hasAttribute('open')) return false;

    var protectedNodes = panelAncestors();

    // Self-heal: if an earlier version hid part of the panel, put it back so the customer does
    // not have to reload the page.
    for (var p = 0; p < protectedNodes.length; p++) {
      var healed = protectedNodes[p];
      if (healed.getAttribute(HIDDEN_ATTR) !== '1') continue;
      healed.style.removeProperty('visibility');
      healed.style.removeProperty('pointer-events');
      healed.removeAttribute('aria-hidden');
      healed.removeAttribute('tabindex');
      healed.removeAttribute(HIDDEN_ATTR);
    }

    var list = launcherCandidates();
    var hidAny = false;

    for (var i = 0; i < list.length; i++) {
      var el = list[i];
      if (el === host) continue;

      // A custom element is a component host, never a launcher glyph.
      if (el.tagName.indexOf('-') !== -1) continue;

      // Ancestor of the chat panel: hiding it would hide the chat itself.
      if (protectedNodes.indexOf(el) !== -1) continue;

      // A launcher is small. This also rejects zero-sized wrappers, which is how the panel's
      // container got caught in the first place.
      var rect = el.getBoundingClientRect();
      if (!(rect.width > 0 && rect.height > 0 && rect.width <= 480 && rect.height <= 160)) continue;

      if (el.getAttribute(HIDDEN_ATTR) === '1' && el.style.getPropertyValue('visibility') === 'hidden') {
        hidAny = true;
        continue;
      }
      el.style.setProperty('visibility', 'hidden', 'important');
      el.style.setProperty('pointer-events', 'none', 'important');
      el.setAttribute('aria-hidden', 'true');
      el.setAttribute('tabindex', '-1');
      el.setAttribute(HIDDEN_ATTR, '1');
      hidAny = true;
    }
    return hidAny;
  }

  /**
   * Reopen through Inbox's own state attribute.
   *
   * After a minimise Inbox removes its launcher entirely, so there is nothing to click — but it
   * still honours `open` on the host (and on `shopify-agent-runtime`). Verified on a live
   * Horizon store: `open` went false -> true and the panel came back.
   */
  function forceOpen(host) {
    if (!host) return false;
    host.setAttribute('open', '');
    if (host.shadowRoot) {
      var runtime = deepQuery(host.shadowRoot, ['shopify-agent-runtime']);
      if (runtime) runtime.setAttribute('open', '');
    }
    return true;
  }

  function openInbox() {
    var host = inboxHost();
    var button = inboxLauncher();

    // 1. Inbox's own launcher first: it is the only path that runs Inbox's state machine, and on
    //    themes that dock the chat (Horizon) the theme opens its drawer in response to it.
    if (button) {
      button.click();
      // While minimised there is no live launcher and the click is a no-op; nudge it via `open`
      // if it has not reacted. The timeout is short so it never fights a successful open.
      if (host) {
        window.setTimeout(function () {
          if (host.isConnected && !host.hasAttribute('open')) host.setAttribute('open', '');
        }, 700);
      }
      return true;
    }

    // 2. No launcher at all (the minimised state) — set Inbox's own open state.
    if (host) return forceOpen(host);

    // 3. A theme-provided drawer trigger, for setups that expose one.
    var trigger = document.querySelector('[data-chat-drawer-open]');
    if (trigger) {
      trigger.click();
      return true;
    }

    // Never open the theme drawer ourselves: that mounts the drawer without Inbox rendering into
    // it, which is the "empty side panel instead of the chat" bug.
    return false;
  }

  function markInboxUnavailable() {
    if (hintEl) hintEl.textContent = 'Live chat is unavailable right now';
    if (warned || !window.console || !console.warn) return;
    warned = true;
    console.warn(
      '[bm-chat] No Shopify Inbox chat found. Enable the "Online store chat" app embed ' +
        '(Customize → App embeds). The WhatsApp item keeps working.'
    );
  }

  function markInboxAvailable() {
    if (hintEl && hintOriginal) hintEl.textContent = hintOriginal;
  }

  if (inboxItem) {
    inboxItem.addEventListener('click', function () {
      setOpen(false);
      // The drawer lookup succeeds even when Inbox is not installed — opening it would show an
      // empty panel, which is a worse outcome than an honest "unavailable" label.
      if (inboxReady() && openInbox()) {
        markInboxAvailable();
      } else {
        markInboxUnavailable();
      }
    });
  }

  /* ================================================== keep the launcher hidden */

  /**
   * Yield the bottom corner while the chat panel is open.
   *
   * Our launcher and Inbox's host were both z-index 2147483000; ours comes later in the DOM, so
   * it painted straight over Inbox's own send button and made the chat feel broken. Lowering our
   * z-index does NOT fix it: <shopify-chat> sits inside the theme's <dialog>, which isolates its
   * stacking context, so our launcher still wins the hit-test. Watching Inbox's own state and
   * stepping aside is the only reliable option.
   *
   * `data-bm-yield="false"` on the root disables it, for merchants who would rather keep the
   * button visible on top of the chat input.
   */
  var scheduled = false;
  var yieldEnabled = root.getAttribute('data-bm-yield') !== 'false';
  var yieldWatchers = { host: null, drawer: null };

  function yieldIsActive() {
    var host = inboxHost();
    if (!host) return false;
    // `open` means the panel is showing. `mode="managed"` alone is NOT enough: Inbox leaves it
    // as "managed" for a while after a minimise, and yielding on that hid our launcher with the
    // chat out of sight — which read as "the button disappeared".
    var drawer = chatDrawer();
    var docked = host.getAttribute('mode') === 'managed' && !!drawer && drawer.hasAttribute('open');
    return host.hasAttribute('open') || docked;
  }

  function syncYield() {
    if (!yieldEnabled) return;
    var active = yieldIsActive();
    root.classList.toggle('bm-chat--yield', active);
    if (active) setOpen(false);
  }

  function bindOpenWatcher() {
    if (!yieldEnabled || typeof MutationObserver === 'undefined') return;

    var host = inboxHost();
    if (!host) return;

    // Re-bind when Inbox replaces its element; a stale observer on a detached node would
    // silently stop yielding and the overlap would come back.
    if (yieldWatchers.host !== host) {
      yieldWatchers.host = host;
      new MutationObserver(syncYield).observe(host, {
        attributes: true,
        attributeFilter: ['open', 'mode'],
      });
    }

    // The docked case is driven by the theme drawer's `open`, not by the host.
    var drawer = chatDrawer();
    if (drawer && yieldWatchers.drawer !== drawer) {
      yieldWatchers.drawer = drawer;
      new MutationObserver(syncYield).observe(drawer, {
        attributes: true,
        attributeFilter: ['open'],
      });
    }

    syncYield();
  }

  function scheduleSync() {
    if (scheduled) return;
    scheduled = true;
    window.requestAnimationFrame(function () {
      scheduled = false;
      observe();
      hideLaunchers();
      bindOpenWatcher();
    });
  }

  // Inbox is injected asynchronously, and it re-creates its launcher inside its shadow tree
  // after a minimise. A single observer attached to document.body never sees those mutations,
  // which is exactly how the launcher came back next to our button. Observe every reachable
  // root, including nested shadow roots, and add new ones as they appear.
  var observedRoots = [];

  function isObserved(root) {
    for (var i = 0; i < observedRoots.length; i++) {
      if (observedRoots[i] === root) return true;
    }
    return false;
  }

  function observeRoot(root) {
    if (!root || isObserved(root)) return;
    observedRoots.push(root);
    new MutationObserver(scheduleSync).observe(root, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['open', 'mode'],
    });
  }

  function observe() {
    if (!('MutationObserver' in window)) return;
    observeRoot(document.documentElement);

    var host = inboxHost();
    if (!host) return;
    observeRoot(host);

    var scope = host.shadowRoot;
    if (!scope) return;
    observeRoot(scope);
    var all = scope.querySelectorAll('*');
    for (var i = 0; i < all.length; i++) {
      if (all[i].shadowRoot) observeRoot(all[i].shadowRoot);
    }
  }

  function start() {
    hideLaunchers();
    observe();
    window.addEventListener('load', scheduleSync);

    // Safety net for the first seconds: the component can mount late without a mutation we
    // happen to observe (e.g. inside a closed shadow root opened later).
    var ticks = 0;
    var timer = window.setInterval(function () {
      ticks += 1;
      scheduleSync();
      // Keep polling until the launchers exist and have been hidden; after that the observers
      // take over, and the tick cap bounds the cost on pages where Inbox never loads.
      if (hideLaunchers() || ticks > 60) window.clearInterval(timer);
    }, 500);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
